import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import useApi from '../hooks/useApi';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/common/Modal';
import ConfirmModal from '../components/common/ConfirmModal';
import PageHeader from '../components/common/PageHeader';
import PageState from '../components/common/PageState';
import { Button } from '../styles/common/Button.styled';
import { ConsoleInput, ConsoleLabel, ConsoleTextArea, FieldError, FormGroup } from '../styles/common/input.styled';
import * as T from '../styles/common/table.styled';

interface ApiKey {
  id: number;
  name: string;
  maskedKey: string;
  allowedIpRanges: string[];
  createdAt: string;
  revokedAt?: string | null;
}

const Help = styled.p`
  margin: 0 0 16px; color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px; line-height: 18px;
`;
const KeyCell = styled(T.Td)<{ $revealed: boolean }>`
  ${({ $revealed }) => $revealed && 'white-space: normal; overflow: visible; overflow-wrap: anywhere;'}
`;
const IpCell = styled(T.Td)`white-space: normal; overflow-wrap: anywhere;`;
const KeyValue = styled.code<{ $revealed: boolean }>`
  display: inline-block; max-width: 100%; vertical-align: bottom;
  color: ${({ theme }) => theme.color.text_color_200}; font: 12px/20px ui-monospace, SFMono-Regular, Menlo, monospace;
  ${({ $revealed }) => $revealed ? 'white-space: normal; overflow-wrap: anywhere;' : 'overflow: hidden; text-overflow: ellipsis;'}
`;
const Actions = styled.div`display: flex; gap: 4px; align-items: center;`;
const Secret = styled.div`
  display: flex; align-items: center; gap: 8px;
  input { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
`;
const Notice = styled.p`
  margin: 0 0 12px; color: ${({ theme }) => theme.color.text_color_200}; font-size: 13px; line-height: 20px;
`;

const parseRanges = (value: string) => value.split(/\r?\n/).map(range => range.trim()).filter(Boolean);
const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('ko-KR');
};
const getErrorMessage = (error: any) => error?.response?.data?.message || '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

export default function ApiKeyManagementPage() {
  const { mode } = useAuth();
  const { request, loading } = useApi<any>();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRanges, setNewRanges] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState('');
  const [revealedApiKeys, setRevealedApiKeys] = useState<Record<number, string>>({});
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [editRanges, setEditRanges] = useState('');
  const [revokingKey, setRevokingKey] = useState<ApiKey | null>(null);

  const fetchKeys = useCallback(async () => {
    setFetching(true);
    setLoadError(false);
    try {
      const result = await request({ url: '/user/api-keys', method: 'GET' });
      setApiKeys(Array.isArray(result) ? result : []);
    } catch {
      setLoadError(true);
    } finally {
      setFetching(false);
    }
  }, [request]);

  useEffect(() => {
    if (mode === 'basic') void fetchKeys();
  }, [fetchKeys, mode]);

  const closeCreate = () => {
    setCreateOpen(false);
    setNewName('');
    setNewRanges('');
    setFieldError('');
  };

  const createKey = async () => {
    if (issuing) return;
    const name = newName.trim();
    if (!name || name.length > 100) {
      setFieldError(!name ? '키 이름을 입력해 주세요.' : '키 이름은 100자 이내로 입력해 주세요.');
      return;
    }
    setFieldError('');
    setIssuing(true);
    try {
      // Do not retain the one-time secret in useApi's cached response state.
      const { data: created } = await api.post('/user/api-keys', { name, allowedIpRanges: parseRanges(newRanges) });
      setCreatedApiKey(created.apiKey);
      setRevealedApiKeys(current => ({ ...current, [created.id]: created.apiKey }));
      setApiKeys(current => [{
        id: created.id,
        name: created.name,
        maskedKey: created.keyPrefix,
        allowedIpRanges: created.allowedIpRanges,
        createdAt: created.createdAt,
      }, ...current]);
      closeCreate();
      toast.success('API KEY를 발급했습니다. 원문을 안전한 곳에 보관해 주세요.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIssuing(false);
    }
  };

  const saveAllowedIps = async () => {
    if (!editingKey) return;
    try {
      await request({
        url: `/user/api-keys/${editingKey.id}/allowed-ip-ranges`,
        method: 'PUT',
        data: { allowedIpRanges: parseRanges(editRanges) },
      });
      setEditingKey(null);
      toast.success('허용 IP 목록을 수정했습니다.');
      void fetchKeys();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const revokeKey = async () => {
    if (!revokingKey) return;
    try {
      await request({ url: `/user/api-keys/${revokingKey.id}/revoke`, method: 'POST' });
      setRevealedApiKeys(current => {
        const next = { ...current };
        delete next[revokingKey.id];
        return next;
      });
      setRevokingKey(null);
      toast.success('API KEY를 폐기했습니다.');
      void fetchKeys();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(createdApiKey);
      toast.success('API KEY를 복사했습니다.');
    } catch {
      toast.error('복사할 수 없습니다. 키를 선택해 직접 복사해 주세요.');
    }
  };

  if (mode !== 'basic') return <Navigate to="/statistics/calls" replace />;

  return <T.PageContainer>
    <PageHeader
      title="본인 API KEY 관리"
      count={!fetching && !loadError ? apiKeys.length : undefined}
      description="외부 연동에 사용할 API KEY를 발급하고 관리합니다."
      actions={<Button onClick={() => setCreateOpen(true)}>API KEY 발급</Button>}
    />
    <Help>새 키의 원문은 발급 직후 현재 화면에 표시되며, 새로고침하거나 페이지를 벗어나면 다시 조회할 수 없습니다. 기존 키는 마스킹되어 표시됩니다. IPv6 요청은 허용되지 않습니다.</Help>

    {fetching ? <PageState status="loading" /> : loadError ? <PageState status="error" onRetry={fetchKeys} /> : apiKeys.length === 0 ? <PageState status="empty" title="발급된 API KEY가 없습니다." description="외부 서비스 연동을 위해 API KEY를 발급해 주세요." /> : (
      <T.TableArea $stickyActions $minWidth={1040}>
        <T.Table>
          <thead><tr>
            <T.Th $width="19%">키 이름</T.Th><T.Th $width="21%">API KEY</T.Th><T.Th $width="28%">허용 IP</T.Th><T.Th $width="17%">발급 일시</T.Th><T.Th $width="8%">상태</T.Th><T.Th $width="180px">관리</T.Th>
          </tr></thead>
          <tbody>{apiKeys.map(key => {
            const revealedKey = revealedApiKeys[key.id];
            return <T.Tr key={key.id}>
            <T.Td title={key.name}>{key.name}</T.Td>
            <KeyCell $revealed={Boolean(revealedKey)} title={revealedKey || key.maskedKey}><KeyValue $revealed={Boolean(revealedKey)}>{revealedKey || key.maskedKey}</KeyValue></KeyCell>
            <IpCell title={key.allowedIpRanges.join(', ')}>{key.allowedIpRanges.length ? key.allowedIpRanges.join(', ') : '제한 없음'}</IpCell>
            <T.Td>{formatDate(key.createdAt)}</T.Td>
            <T.Td><T.Badge $tone={key.revokedAt ? 'neutral' : 'success'}>{key.revokedAt ? '폐기됨' : '사용 중'}</T.Badge></T.Td>
            <T.Td><Actions>
              {!key.revokedAt && <>
                <T.ActionButton $variant="secondary" onClick={() => { setEditingKey(key); setEditRanges(key.allowedIpRanges.join('\n')); }}>허용 IP 수정</T.ActionButton>
                <T.ActionButton $variant="danger" onClick={() => setRevokingKey(key)}>폐기</T.ActionButton>
              </>}
            </Actions></T.Td>
          </T.Tr>;
          })}</tbody>
        </T.Table>
      </T.TableArea>
    )}

    {isCreateOpen && <Modal title="API KEY 발급" onClose={closeCreate} closeDisabled={loading || issuing} width={520} footer={<>
      <Button $variant="secondary" onClick={closeCreate} disabled={loading || issuing}>취소</Button>
      <Button onClick={createKey} disabled={loading || issuing}>{issuing ? '발급 중...' : '발급하기'}</Button>
    </>}>
      <FormGroup>
        <ConsoleLabel htmlFor="api-key-name">키 이름 *</ConsoleLabel>
        <ConsoleInput id="api-key-name" aria-label="키 이름" maxLength={100} value={newName} onChange={event => setNewName(event.target.value)} aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? 'api-key-name-error' : undefined} placeholder="예: CRM 운영 연동" />
        {fieldError && <FieldError id="api-key-name-error" role="alert">{fieldError}</FieldError>}
      </FormGroup>
      <FormGroup>
        <ConsoleLabel htmlFor="api-key-allowed-ips">허용 IP 목록</ConsoleLabel>
        <ConsoleTextArea id="api-key-allowed-ips" aria-label="허용 IP 목록" value={newRanges} onChange={event => setNewRanges(event.target.value)} placeholder={'IPv4 주소 또는 CIDR을 한 줄에 하나씩 입력\n예: 203.0.113.12\n예: 198.51.100.0/24'} />
        <Help>비워 두면 IPv4 제한 없이 사용할 수 있습니다. IPv6 요청은 항상 거부됩니다.</Help>
      </FormGroup>
    </Modal>}

    {editingKey && <Modal title="허용 IP 수정" onClose={() => setEditingKey(null)} closeDisabled={loading} width={520} footer={<>
      <Button $variant="secondary" onClick={() => setEditingKey(null)} disabled={loading}>취소</Button>
      <Button onClick={saveAllowedIps} disabled={loading}>{loading ? '저장 중...' : '변경 저장'}</Button>
    </>}>
      <Notice><strong>{editingKey.name}</strong> 키에서 접근을 허용할 IPv4 주소 또는 CIDR을 입력하세요.</Notice>
      <FormGroup>
        <ConsoleLabel htmlFor="api-key-edit-allowed-ips">허용 IP 목록</ConsoleLabel>
        <ConsoleTextArea id="api-key-edit-allowed-ips" aria-label="허용 IP 목록" value={editRanges} onChange={event => setEditRanges(event.target.value)} placeholder="한 줄에 하나씩 입력" />
        <Help>비워 두면 IPv4 제한 없이 사용할 수 있습니다. IPv6 요청은 항상 거부됩니다.</Help>
      </FormGroup>
    </Modal>}

    {createdApiKey && <Modal title="API KEY가 발급되었습니다" onClose={() => setCreatedApiKey('')} width={520} footer={<>
      <Button $variant="secondary" onClick={copyKey}>복사</Button>
      <Button onClick={() => setCreatedApiKey('')}>확인했어요</Button>
    </>}>
      <Notice>키 원문은 현재 화면의 목록에도 표시됩니다. 새로고침하거나 페이지를 벗어나면 다시 확인할 수 없으니 지금 복사해 안전한 곳에 보관해 주세요.</Notice>
      <Secret><ConsoleInput aria-label="발급된 API KEY" value={createdApiKey} readOnly /></Secret>
    </Modal>}

    {revokingKey && <ConfirmModal
      title="API KEY 폐기"
      message={`“${revokingKey.name}” 키를 폐기하면 다시 사용할 수 없습니다. 계속하시겠습니까?`}
      confirmLabel="폐기하기"
      tone="danger"
      pending={loading}
      onCancel={() => setRevokingKey(null)}
      onConfirm={revokeKey}
    />}
  </T.PageContainer>;
}
